import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import ReactPlayer from 'react-player/file';
import * as screenfull from 'screenfull';
import cx from 'classnames';
import styles from './Player.module.scss';
import './rc-slider.scss';
import { RoundedPlayIcon } from '../Player/VideoControls';
import Duration from './Duration';
import { useTrimContext } from '../../context/VideoTrimContex';
import CmsAPI from '../../../common/apis/CmsAPI';
import { LoadingSpinner } from '../../components/Icon/icons';
import { ProgressLogo } from '../ProgressLogo/ProgressLogo';
import PlayerControls from '../PlayerControls/PlayerControls';
import useWindowSize from '../../hooks/useWindowSize';
import PlayerBackground from '../PlayerBackground/PlayerBackground';
import PlayerThumbnail from '../PlayerThumbnail/PlayerThumbnail';
import VideoLogo from '../VideoLogo/VideoLogo';
import VideoTextPanel from '../VideoTextPanel/VideoTextPanel';
import { useTranslation } from 'react-i18next';
import TransitionSimulation from '../TransitionSimulation/TransitionSimulation';
import FadeInTransition from '../FadeInTransition/FadeInTransition';
import { CustomButton } from '../Button/Button';
import InitialVideoSpeedMenu from '../InitialVideoSpeedMenu/InitialVideoSpeedMenu';
import VideoCallToAction from '../VideoCallToAction/VideoCallToAction';
import { useCallToActionContext } from '../../context/CallToActionContext';
import { useChaptersContext } from '../../context/ChaptersContext';
import { useZoomContext } from '../../context/ZoomContext';
import EmptyPlayer from './EmptyPlayer';
import useThrottledValue from '../../hooks/useThrottledValue';
import { getPlayerMixedOrientation, getMaxDimensions } from '../../utils/SimulatedItemsUtility';
import { useTextPanelContext } from '../../context/TextPanelContext';
import { Helmet } from 'react-helmet';
import { ZOOM_TYPES } from '../../features/ZoomVideo/constants';
import { useLanguageContext } from '../../context/LanguageContext';
import { isVideoPathIsHls } from '../../utils/videoUtils';
import { useVideoProgressContext } from '../../context/VideoProgressContext';
import { useVoiceoverContext } from '../../context/VoiceoverContext';
import VoiceoverPlayer from '../VoiceoverPlayer/VoiceoverPlayer';
import Countdown from '../Countdown/Countdown';
import * as Sentry from '@sentry/browser';
import { ENTER_KEY_CODE, SPACE_KEY_CODE } from '../../constants/common';
import InPlayerCommentsOverlay from '../../features/VideoDetails/components/InPlayerCommentsOverlay/InPlayerCommentsOverlay';
import { useInPlayerCommentContext } from '../../context/InPlayerCommentContext';
import { useNodeRef } from 'engage/hooks/useNodeRef';
import { throttle } from 'engage/utils/general';
import CE_EU_AI_ACT_icon from '../../assets/ceEUAIActIcon.png';
import InfoTooltip from '../../features/Home/components/InfoTooltip/InfoTooltip';

const EXTRA_WIDE_SCREEN_RATIO = 1.8;
const HLS_JS_LIBRARY_VERSION = '1.5.13';
// Min remaining value between duration and currentTime, to skip onPause event that could
// be fired right before onEnded call withing <video/> HTML component
const REMAINING_TO_PREVENT_PAUSE_BEFORE_END = 0.05;

const defaultSetting = {
  pip: false,
  loop: false,
};

const isSafari =
  /constructor/i.test(window.HTMLElement) ||
  (function (p) {
    return p.toString() === '[object SafariRemoteNotification]';
  })(!window['safari'] || (typeof safari !== 'undefined' && safari.pushNotification));

function iOS() {
  const iDevices = ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'];

  // eslint-disable-next-line no-extra-boolean-cast
  if (!!navigator.platform) {
    while (iDevices.length) {
      if (navigator.platform === iDevices.pop()) {
        return true;
      }
    }
  }

  return false;
}

const initProgress = {
  loaded: 0,
  loadedSeconds: 0,
  played: 0,
  playedSeconds: 0,
};

const initSliderWidth = {
  min: 0,
  max: 1,
};

const FADE_IN_DURATION = 250;

const POSSIBLE_TRIMMING_DEVIATION_COEF = 0.005; // 5% from trimmed duration
const TRIMMING_DEVIATION = 30; // value in milliseconds

const RENDERING_PROCESS = {
  PENDING: 1,
  SHOW_DONE: 2,
};

export default function Player(props) {
  const {
    uuid = null,
    analytics = true,
    scenes,
    styling = {},
    src,
    native,
    poster,
    onlyResourceThumbnail = false,
    onDownloadVideo,
    error,
    errorComponent,
    onVideoStart,
    onTick,
    onEnded = () => { },
    videoDuration,
    overlay = false,
    overlayContent = null,
    light = false,
    playOnLoad = false,
    onlyPlayControlShow = false,
    handleProgressChange = () => { },
    restartPlayerOnEnd = false,
    hideSlider = false,
    hidePlayControl = false,

    playOnStart = false,

    processing,
    processingMessage = null,
    playerOnly,

    seekToSecondExternal = null,
    isFullScreenAllowed = true,

    hasHls,
    status,
    enableSubtitles = true,
    subtitles = null,
    subtitlesSettings = null,
    showSubtitlesExistance = false,
    controlsVisibilityChanged = () => { },
    logo = null,
    customTiming = null,
    multiplyMode = false,
    active = true,
    handleTogglePlaying = () => { },
    textPanels = null,
    transitionsArray = null,
    externalVolume = null,
    handleVolumeUpdated = () => { },
    bufferingDelay = 0,
    showSubtitlesControlExternal = false,
    externalLanguage = null,
    externalAvailableLanguages = null,
    handleLanguageUpdated = null,
    intro = null,
    outro = null,
    disableDownload = false,
    downloadNotification,
    blockPlaying = false,
    isEmbeddedMode = false,
    hideControlsOnStart = false,
    isDraft = false,
    fadeIn = true,
    companyConfig = null,
    onSeekCallback = () => { },
    externalPlaybackRate = null,
    handleExternalPlaybackRateChange = () => { },
    isBackwardAndForwardActionsAllows = true,
    videoVolumeWithMusic = 1,
    isLazyLoadingContent = true,
    rerecordComponent,
    isFirstPlayedExternal,
    handleStartBackgroundMusic = () => { },
    handleStopBackgroundMusic = () => { },
    isSharedPage,
    isSingleAvailableScene = false,
    onEndComponent,
    directPlayerVolume = null,
    blockSimulatedResources = false,
    videoConfig = {},
    containsAIContent = false,
    aiDisclosureTooltip = null,

  } = props;

  const {
    trimmingOn,
    startTrimStamp,
    endTrimStamp,
    updateStartTrimStamp,
    updateEndTrimStamp,
    updateCurrentProgress,
    updateTrimmingVideoDuration,
    trimmingProgress,
    videoTrimmed,
    updateVideoTrimmed,
    controlInUse,
  } = useTrimContext();

  const { playerWrapperClassName = '', playerClassName = '', moduleClassName = '' } = styling;
  const [playing, setPlaying] = useState(playOnLoad);
  const [showError, setShowError] = useState(!isSharedPage && status === 'FAILED');

  // Indicates if the source is HLS. Used for cases when in single player mode original video file should be played
  const [isHlsSource] = useState(isVideoPathIsHls(src));

  const isIOSDevice = useMemo(() => iOS());
  const mediaMatch = window.matchMedia('(max-width: 900px)');
  const [isMobileView] = useState(mediaMatch.matches);
  const [hideControls, setHideControls] = useState(!isMobileView);
  const seeking = useRef(false);
  const [volume, setVolume] = useState({ current: playOnLoad ? 0 : 1, previously: 1 });
  const [duration, setDuration] = useState(0);
  const [buffering, setBuffering] = useState(false);
  const [renderingProcess, setRenderingProcess] = useState(null);
  const [trimmedDuration, setTrimmedDuration] = useState(0);
  const [realDuration, setRealDuration] = useState(0);
  const [videoContentDimensions, setVideoContentDimensions] = useState(
    {
      leftOffset: 0,
      topOffset: 0,
      margins: {
        x: { min: 0, max: 100 },
        y: { min: 0, max: 100 }
      }
    }
  );

  const sceneData = useMemo(() => {
    if (scenes && !multiplyMode && !isHlsSource) {
      return scenes[0];
    }
    return structuredClone(scenes);
  }, [scenes, isHlsSource, multiplyMode]);

  const bufferTimeout = useRef(null);
  const controlsTimeout = useRef(null);
  const durationProcessingTimeout = useRef(null);

  const isVideoEndCtaEnabled = useMemo(() => {
    const videoEndCta = companyConfig?.features?.find(feature => feature.name === 'web_video_end_cta');
    return videoEndCta?.enabled ?? false;
  }, [companyConfig]);

  const mixedOrientation = useMemo(() => (sceneData?.length ? getPlayerMixedOrientation(sceneData) : false), [sceneData]);

  const trimmingDeviation = useMemo(() => {
    const deviation = (sceneData?.trimEnd - sceneData?.trimStart) * POSSIBLE_TRIMMING_DEVIATION_COEF ?? TRIMMING_DEVIATION;
    return Math.min(deviation, TRIMMING_DEVIATION);
  }, [duration, sceneData]);
  /**
   * As to the Video Player component sends just a video URL, it's defines video duration by itself
   * So, for correct seekTo() and video progress showing, realDuration value are required
   * */
  const [{ loaded, loadedSeconds, played, playedSeconds }, setVideoProgress] = useState(initProgress);
  const [started, setStarted] = useState(false);
  const [ready, setReady] = useState(false);
  const { width, height } = useWindowSize();
  const [currentLanguage, setCurrentLanguage] = useState('');
  const videoElement = useRef(null);
  const resizeObserverRef = useRef(null);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [overallPlaybackRate, setOveralPlaybackRate] = useState(1);
  const [nextSceneTransition, setNextSceneTransition] = useState(false);
  const [iosFullscreen, setIosFullscreen] = useState(false);

  const { nodeRef: playerParent, handleNodeRef } = useNodeRef();

  const player = useRef(null);
  const firstPlay = useRef(false);
  const renderingDoneRef = useRef(null);
  const mouseMovementRef = useRef({ latestTimestamp: null });

  const { addNotesMode, setInitPosition } = useInPlayerCommentContext();

  const { isChaptersEditor, updateChapterStart } = useChaptersContext();
  const { editorSceneId } = useTextPanelContext();
  const { t } = useTranslation();
  const {
    isCallToActionOpened,
    callActions,
    callActionsSeekValue,
    isNewActionTimeline,
    changeIsNewActionTimeline,
  } = useCallToActionContext();

  const {
    isVoiceoverOpened,
    voiceoverSeekValue,
    isVoiceoverRecording,
    isVoiceoverCountdown,
    changeIsVoiceoverRecording,
    changeIsVoiceoverCountdown,
    changeIsNewVoiceoverSeekValue,
  } = useVoiceoverContext();

  const { language: appLanguage } = useLanguageContext();

  const { updateVideoProgress, videoSeekToValue, updateVideoSeekToValue } = useVideoProgressContext();
  const { isZoomOpened, updateZoomProgress, zoomsSeekValue, zooms } = useZoomContext();

  const isSimulatedItemExists = useMemo(() => logo || textPanels || subtitles || callActions, [textPanels, subtitles, callActions, logo]);

  const videoMaxDimensions = useMemo(() => {
    const internalScenes = structuredClone(scenes);
    if (internalScenes && videoElement?.current && player?.current && isSimulatedItemExists) {
      if (internalScenes.length === 1 && !internalScenes[0].dimensions.width) {
        internalScenes[0].dimensions = {
          width: videoElement.current.videoWidth,
          height: videoElement.current.videoHeight,
        };
      }
      return getMaxDimensions(internalScenes);
    }
    return null;
  }, [videoElement?.current?.videoWidth, scenes, isSimulatedItemExists]);

  const { updateValue: updateShowRerecording, value: showRerecording } = useThrottledValue({ timeout: 100 });

  const additionalProps = useMemo(() => {
    const config = {};
    if (!multiplyMode) {
      config.file = {
        attributes: { crossOrigin: 'false', preload: isLazyLoadingContent ? 'metadata' : 'auto' },
        hlsOptions: {
          autoStartLoad: src !== null,
        },
      };
      config.hlsVersion = HLS_JS_LIBRARY_VERSION;
    }
    return { config };
  }, [isLazyLoadingContent, multiplyMode, src]);

  const { pip, loop } = defaultSetting;

  const showOverlay = overlay && !(multiplyMode ? isFirstPlayedExternal : firstPlay.current);

  const sliderWidth = useMemo(() => {
    const durationInMs = isFinite(realDuration) && realDuration ? realDuration * 1000 : videoDuration;
    if (sceneData && sceneData.trimEnd > 0 && sceneData.trimStart !== sceneData.trimEnd) {
      return { min: sceneData.trimStart / durationInMs, max: sceneData.trimEnd / durationInMs };
    } else {
      return initSliderWidth;
    }
  }, [sceneData, realDuration]);

  const isRenderingDone = useMemo(() => renderingProcess === RENDERING_PROCESS.SHOW_DONE, [renderingProcess]);

  const isExtraWideScreen = useMemo(() => parseInt(width) / parseInt(height) > EXTRA_WIDE_SCREEN_RATIO, [width, height]);

  const calculatedPlayedValue = useMemo(() => {
    let seconds = 0;

    seconds = playedSeconds / (sceneData?.playbackRate || 1);
    if (sceneData?.trimStart !== sceneData?.trimEnd) {
      const trimStartSeconds = (trimmingOn && active ? startTrimStamp : sceneData?.trimStart) / 1000;
      seconds -= trimStartSeconds / (sceneData?.playbackRate || 1);
    }

    const maxDuration = (videoDuration ? videoDuration : realDuration * 1000) / (sceneData?.playbackRate || 1);
    return Math.min(Math.max(seconds, 0), maxDuration);
  }, [playedSeconds, active]);

  // Hide thumbnail only if the zoom tab is open
  // and the current time is not 0 (the user manually selected any other)
  const isShowThumbnailWhileZoomOpened = useMemo(
    () => !isZoomOpened || playedSeconds * 1000 - sceneData?.trimStart < TRIMMING_DEVIATION,
    [isZoomOpened, playedSeconds]
  );

  const reversedZoomPoints = useMemo(() => sceneData?.zoomPoints?.sort((a, b) => a.offset - b.offset).reverse() || [], [
    sceneData?.zoomPoints,
  ]);

  const currentZoomStyles = useMemo(() => {
    if (!multiplyMode || (!isZoomOpened || zooms.length == 0) && sceneData?.zoomPoints?.length == 0) {
      return null;
    }
    const playedMilliseconds = playedSeconds * 1000;

    let zoomStyles;
    let prevZoomStyles;

    if (isZoomOpened) {
      // We changed the zoom values based on the current scene settings (scene start and scene speed) and these values were mutated
      // So we need to convert back to real values.
      const realZooms = zooms?.map(zoom => {
        const realOffset = zoom.offset * (sceneData?.playbackRate || 1) + sceneData?.trimStart;
        const realDuration = zoom.duration * (sceneData?.playbackRate || 1);

        return {
          ...zoom,
          offset: realOffset,
          duration: realDuration,
        };
      });

      zoomStyles = realZooms?.find(({ offset, duration }) => offset <= playedMilliseconds && offset + duration >= playedMilliseconds);

      // This value is needed to know the margins of the previous zoom for the correct transition of the zoom out effect
      prevZoomStyles = realZooms
        ?.sort((a, b) => a.offset - b.offset)
        ?.reverse()
        ?.find(({ offset }) => playedMilliseconds > offset);
    } else {
      zoomStyles = sceneData?.zoomPoints?.find(
        ({ offset, duration }) => offset <= playedMilliseconds && offset + duration >= playedMilliseconds
      );
      // This value is needed to know the margins of the previous zoom for the correct transition of the zoom out effect
      prevZoomStyles = reversedZoomPoints.find(({ offset }) => playedMilliseconds > offset);
    }

    const finalStyles = {
      transition:
        (zoomStyles?.zoomType ?? prevZoomStyles?.zoomType) === ZOOM_TYPES.SOFT.type
          ? 'transform 750ms ease-in-out'
          : 'transform 0ms linear',
    };
    const { margins } = videoContentDimensions;

    let marginX = Math.min(margins.x.max, Math.max(margins.x.min, zoomStyles ? zoomStyles.marginX : prevZoomStyles?.marginX));
    let marginY = Math.min(margins.y.max, Math.max(margins.y.min, zoomStyles ? zoomStyles.marginY : prevZoomStyles?.marginY));

    finalStyles.transformOrigin = `${marginX}% ${marginY}%`;

    if (zoomStyles) {
      finalStyles.transform = `scale(${zoomStyles.scale})`;
    }

    return finalStyles;
  }, [sceneData?.zoomPoints, playedSeconds, isZoomOpened, zooms, reversedZoomPoints, videoContentDimensions]);

  const currentCallAction = useMemo(() => {
    const playedMilliseconds = playedSeconds * 1000;
    return callActions?.find(({ offset, duration }) => offset <= playedMilliseconds && offset + duration >= playedMilliseconds);
  }, [playedSeconds, callActions]);

  const durationToControls = useMemo(() => {
    if (trimmingOn && active) {
      return trimmedDuration < 1 ? Math.round(trimmedDuration) : trimmedDuration;
    } else {
      // If player source in single video mode is not hls, that means, that player got the original file instead of hls.
      // The video could be trimmed (from mobile), which means the original file should correspond trimming values, return
      // duration from video details instead if the original video duration.
      // At the same time, just recorded video (from extension) can have no duration at the point player loads,
      // so in this case real video duration should be returned
      if (!multiplyMode && !isHlsSource && duration) {
        return duration;
      }
      return duration ? duration : realDuration;
    }
  }, [trimmingOn, trimmedDuration, duration, realDuration, active, multiplyMode]);

  /**
   * Update progress considering full video duration, and duration received from API (trimmed one)
   * */
  const setProgress = useCallback(
    newProgress => {
      if (isSafari && newProgress.played > 1 && !sceneData?.trimStart) {
        newProgress.played = 1;
      }

      const durationSec = (isFinite(realDuration) && realDuration > 0) ? realDuration : videoDuration / 1000;

      let newProgressPlayed = newProgress.played;
      if (!newProgressPlayed && newProgress.playedSeconds) {
        newProgressPlayed = newProgress.playedSeconds / durationSec;
      }
      setVideoProgress(prevState => ({
        ...prevState,
        ...newProgress,
        ...{ played: newProgressPlayed },
      }));
      if (active) {
        if (newProgressPlayed > 1 && sceneData?.trimStart) {
          updateCurrentProgress((sceneData.trimStart / durationSec) * 1000);
        } else {
          updateCurrentProgress(newProgressPlayed);
        }
      }

      updateVideoProgress({
        milliseconds: newProgress?.playedSeconds ? newProgress?.playedSeconds * 1000 : newProgressPlayed * durationSec * 1000,
        percents: newProgressPlayed,
      });

      active && newProgress?.playedSeconds && handleProgressChange(newProgress.playedSeconds);

      if (active && multiplyMode) {
        updateZoomProgress({
          percents: ((newProgress.playedSeconds * 1000 || sceneData?.trimStart) - sceneData?.trimStart) / videoDuration || 0,
          milliseconds: newProgress?.playedSeconds * 1000 || 0,
        });
      }

      if (isNewActionTimeline) {
        changeIsNewActionTimeline(false);
      }
    },
    [playing, realDuration, isNewActionTimeline, active, sceneData, videoDuration, multiplyMode]
  );

  const onStart = useCallback(() => {
    setStarted(true);
  }, []);

  const togglePlaying = useCallback(() => {
    if (!isDraft && !showError) {
      setPlaying(prevState => (prevState ? !prevState : ready && (duration > 0 || realDuration > 0) ? !prevState : false));
    }
  }, [isDraft, showError, ready, duration, realDuration]);

  const keyboardBehavior = useCallback(
    event => {
      if (event.keyCode === SPACE_KEY_CODE && !addNotesMode && !isVoiceoverRecording && !isVoiceoverCountdown) togglePlaying();
    },
    [togglePlaying, isVoiceoverRecording, isVoiceoverCountdown, addNotesMode]
  );

  const toggleMuted = useCallback(() => {
    setVolume(prevState => ({
      current: prevState.current ? 0 : prevState.previously,
      previously: prevState.current,
    }));
  }, []);

  const handleChangeVolume = useCallback(value => {
    console.log(value);
    setVolume(prevState => ({ current: parseFloat(value), previously: prevState.current }));
  }, []);

  const handleChangePlaybackRate = useCallback(value => {
    setPlaybackRate(Number(value));
    handleExternalPlaybackRateChange(Number(value));
  }, []);

  const onPlay = useCallback(() => {
    // Seek to start in case playing original file with trimming
    if (!multiplyMode && sceneData?.trimEnd && playedSeconds * 1000 >= sceneData?.trimEnd - trimmingDeviation) {
      player.current.seekTo(parseInt(sceneData.trimStart / 1000), 'seconds');
    }
    setPlaying(true);
    handleTogglePlaying(true);

    // Only store analytics on first play, if a uuid is set, and if analytics is enabled
    if (!firstPlay.current && uuid !== null && analytics) {
      // First time we're playing this video in this browser session,
      // check if we've viewed this video before by reading localstorage
      const videoViews = JSON.parse(localStorage.getItem('videoViews')) || [];

      // Remove video views > 24h old
      const filteredVideoViews = videoViews.filter(({ viewedAt }) => Date.now() - viewedAt < 1000 * 60 * 60 * 24);
      const isRecentlyViewed = filteredVideoViews.find(({ videoId }) => videoId === uuid);

      if (!isRecentlyViewed) {
        // Check if we have a logged in user, and if so, store the username so we can check
        // on the CMS end if this user is viewing his own video
        const { username } = JSON.parse(localStorage.getItem('userDetails')) || {};

        filteredVideoViews.push({ viewedAt: Date.now(), videoId: uuid });
        localStorage.setItem('videoViews', JSON.stringify(filteredVideoViews));

        // Register view in database
        CmsAPI.logEvent(uuid, { eventType: 'views' });
      }
    }

    if (!firstPlay.current) {
      firstPlay.current = true;
    }
  }, [onVideoStart, sceneData, trimmingDeviation, playedSeconds]);

  const onPause = useCallback(() => {
    if (isVoiceoverRecording || isVoiceoverCountdown) {
      return;
    }

    // To prevent firing onPause right before onEnded from EmptyPlayer component
    if (player.current.duration - player.current.currentTime < REMAINING_TO_PREVENT_PAUSE_BEFORE_END) {
      return;
    }
    setPlaying(false);
    if (
      active &&
      sceneData?.trimEnd &&
      (player.current.currentTime * 1000 <= sceneData?.trimEnd - trimmingDeviation || player.current.currentTime * 1000 > 0)
    ) {
      handleTogglePlaying(false);
    }
    !multiplyMode && handleTogglePlaying(false);
    setBuffering(false);
    if (bufferTimeout.current) {
      clearTimeout(bufferTimeout.current);
      bufferTimeout.current = null;
    }
    if (!active) {
      mouseMovementRef.current.latestTimestamp = null;
    }
  }, [active, sceneData, trimmingDeviation, isVoiceoverRecording, isVoiceoverCountdown]);

  const onSeekMouseDown = useCallback(() => {
    console.log('on Mouse Down');
    if (isMobileView) {
      if (controlsTimeout.current) {
        clearTimeout(controlsTimeout.current);
        controlsTimeout.current = null;
      }
    }
    seeking.current = true;
  }, []);

  const onSeekChange = useCallback(
    value => {
      setProgress({ played: parseFloat(value) });
      handleProgressChange(parseFloat(value) * 1000);
    },
    [setProgress, handleProgressChange]
  );

  const onSeekMouseUp = useCallback(
    value => {
      console.log('on Mouse Up');
      seeking.current = false;
      const seekToSeconds = (isFinite(realDuration) && realDuration ? realDuration : videoDuration / 1000) * value;
      player.current?.seekTo(parseFloat(seekToSeconds), 'seconds');
      if (isMobileView) {
        controlsTimeout.current = setTimeout(() => {
          setHideControls(true);
          controlsTimeout.current = null;
        }, 3000);
      }
    },
    [duration, realDuration, videoDuration, isMobileView]
  );

  const onProgress = useCallback(
    state => {
      // We only want to update time slider if we are not currently seeking
      if (!seeking.current) {
        setProgress(state);
        if (onTick && playing) onTick(state);
      }
    },
    [seeking, setProgress, onTick, playing]
  );

  const onEndedCallback = useCallback(() => {
    if (isVoiceoverRecording) {
      changeIsVoiceoverRecording(false);
    }

    if (isVoiceoverCountdown) {
      changeIsVoiceoverCountdown(false);
    }

    console.log('onEndedCallback');
    if (trimmingOn || isZoomOpened || isVoiceoverOpened) {
      setPlaying(false);
      handleTogglePlaying(false);
    } else if (!onEnded && restartPlayerOnEnd) {
      player.current?.seekTo(parseFloat(sceneData.trimStart / 1000), 'seconds');
      setPlaying(false);
      if (multiplyMode) {
        setNextSceneTransition(true);
        setTimeout(() => {
          setNextSceneTransition(false);
        }, 300);
      }
    }
    if (!multiplyMode && !isHlsSource) {
      setPlaying(false);
    }
  }, [trimmingOn, isZoomOpened, onEnded, multiplyMode, isVoiceoverRecording]);

  const onDuration = useCallback(
    dur => {
      console.log('onDuration', dur);
      // Recorded from web extension .webm original video file has broken duration metadata, so
      // parsed by video player it becomes Infinity. Obviously with this value it's impossible
      // to define playing progress and seek to the value from slider, there is workaround to make
      // player define video duration - seek to enormous big value, wait until player defines end
      // of the file (aka duration)
      if (dur === Infinity && !videoDuration) {
        setReady(false);
        const video = player.current.getInternalPlayer();

        durationProcessingTimeout.current = setTimeout(() => {
          setRenderingProcess(RENDERING_PROCESS.PENDING);
          durationProcessingTimeout.current = null;
        }, 500);

        video.ondurationchange = () => {
          if (durationProcessingTimeout.current === null) {
            setRenderingProcess(RENDERING_PROCESS.SHOW_DONE);
            renderingDoneRef.current = setTimeout(() => {
              setRenderingProcess(null);
              renderingDoneRef.current = null;
            }, 3000);
          } else {
            clearTimeout(durationProcessingTimeout.current);
          }
          video.currentTime = 0;
          setReady(true);
          setRealDuration(video.duration);
          !multiplyMode && updateTrimmingVideoDuration(dur / (sceneData?.playbackRate || 1));
        };
        video.currentTime = 1e10;
      } else {
        setRealDuration(dur);
        !multiplyMode && updateTrimmingVideoDuration(dur / (sceneData?.playbackRate || 1));
      }
    },
    [player, duration]
  );

  const onReady = useCallback(() => {
    setReady(true);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (isIOSDevice) {
      setIosFullscreen(prevState => !prevState);
    } else {
      screenfull.toggle(playerParent);
    }
  }, [videoElement.current]);

  const checkMouseMovement = () => {
    const currentTime = Date.now();
    if (!mouseMovementRef.current.latestTimestamp && !hideControlsOnStart && played < 150) {
      setHideControls(false);
    }
    if (mouseMovementRef.current.latestTimestamp && currentTime - mouseMovementRef.current.latestTimestamp > 3000) {
      setHideControls(true);
    }
  };

  const mouseMoveHandler = () => {
    !(hideControlsOnStart && playedSeconds < 150) && setHideControls(false);
    mouseMovementRef.current.latestTimestamp && setHideControls(false);
    mouseMovementRef.current.latestTimestamp = Date.now();
  };

  const handleOnMouseOver = useCallback(() => {
    if ((!isMobileView || isEmbeddedMode) && !(isIOSDevice && videoElement?.current?.webkitDisplayingFullscreen)) {
      if (mouseMovementRef.current.latestTimestamp) {
        setHideControls(false);
      }
      if (!mouseMovementRef.current.intervalId) {
        mouseMovementRef.current.intervalId = setInterval(checkMouseMovement, 1000);
      }
    }
  }, [hideControls, isIOSDevice, videoElement.current]);

  const handleOnMouseLeave = useCallback(() => {
    if ((!isMobileView || isEmbeddedMode) && !(isIOSDevice && videoElement?.current?.webkitDisplayingFullscreen) && playing) {
      setHideControls(true);
      if (mouseMovementRef.current.intervalId) {
        clearInterval(mouseMovementRef.current.intervalId);
        mouseMovementRef.current.intervalId = null;
      }
    }
  }, [hideControls, isIOSDevice, videoElement.current, playing]);

  const onLanguageSelected = useCallback(
    language => {
      if (enableSubtitles) {
        setCurrentLanguage(language);
      } else {
        handleLanguageUpdated && handleLanguageUpdated(language);
      }
    },
    [setCurrentLanguage, enableSubtitles]
  );

  const handleBufferingStarted = useCallback(() => {
    if (!buffering && bufferingDelay > 0) {
      if (bufferTimeout.current === null) {
        bufferTimeout.current = setTimeout(() => {
          setBuffering(true);
        }, bufferingDelay);
      }
    } else {
      setBuffering(true);
    }
  }, [bufferTimeout.current, bufferingDelay, buffering, setBuffering]);

  const handleBufferingEnded = useCallback(() => {
    setBuffering(false);
    if (bufferTimeout.current) {
      clearInterval(bufferTimeout.current);
      bufferTimeout.current = null;
    }
  }, [bufferTimeout.current]);

  const onSeek = seekTime => {
    if (active) {
      onSeekCallback && onSeekCallback(seekTime);
      changeIsNewVoiceoverSeekValue(true);
    }
  };

  const openCrisp = () => {
    window.$crisp.push(['do', 'chat:show']);
    window.$crisp.push(['do', 'chat:open']);
  };

  const closeError = () => {
    setShowError(false);
    setRenderingProcess(null);
  };

  const handlePlayerClick = useCallback(e => {
    e.stopPropagation();
    if (isVoiceoverRecording || isVoiceoverCountdown) {
      return;
    }

    if (addNotesMode) {
      setInitPosition({ y: e.clientY, x: e.clientX });
      return;
    }

    if (blockPlaying) {
      setPlaying(false);
      handleTogglePlaying(false);
      return;
    }

    if (isMobileView && !isEmbeddedMode) {
      if (!firstPlay.current || !playing) {
        togglePlaying();
        setHideControls(false);
        controlsTimeout.current = setTimeout(() => {
          setHideControls(true);
          controlsTimeout.current = null;
        }, 3000);
      } else {
        if (controlsTimeout.current) {
          clearTimeout(controlsTimeout.current);
          controlsTimeout.current = null;
          setHideControls(true);
        } else {
          setHideControls(false);
          controlsTimeout.current = setTimeout(() => {
            setHideControls(true);
            controlsTimeout.current = null;
          }, 3000);
        }
      }
    } else {
      togglePlaying();
    }
  }, [addNotesMode, blockPlaying, isMobileView, isEmbeddedMode, togglePlaying, handleTogglePlaying]);

  useEffect(() => {
    if (trimmingOn && active) {
      setTrimmedDuration((endTrimStamp - startTrimStamp) / 1000);
      if (played * realDuration * 1000 >= endTrimStamp * (sceneData?.playbackRate || 1)) {
        player.current?.seekTo(parseFloat((endTrimStamp * (sceneData?.playbackRate || 1)) / 1000), 'seconds');
        setPlaying(false);
      }
    }
  }, [endTrimStamp, startTrimStamp, trimmingOn, active]);

  useEffect(() => {
    setDuration(videoDuration / 1000);
    if (active) {
      sceneData?.trimEnd > 0 && sceneData?.trimStart === sceneData?.trimEnd && Sentry.captureMessage(`sceneData.trimStart === sceneData.trimEnd for uuid: ${uuid}, trimStart=${sceneData.trimStart}, trimEnd=${sceneData.trimEnd}`);
      sceneData?.trimEnd && updateEndTrimStamp(sceneData.trimEnd / (sceneData?.playbackRate || 1));
      if (sceneData?.trimStart && videoDuration > 0) {
        updateStartTrimStamp(sceneData.trimStart / (sceneData?.playbackRate || 1));
      } else {
        updateStartTrimStamp(0);
      }
    }
  }, [videoDuration]);

  useEffect(() => {
    return () => {
      mouseMovementRef.current.intervalId && clearInterval(mouseMovementRef.current.intervalId);
      controlsTimeout.current && clearTimeout(controlsTimeout.current);
      renderingDoneRef.current && clearTimeout(renderingDoneRef.current);
    };
  }, []);

  // Controls seeking player from trimming/zoom/cta controls, from chapters and external seek to value
  useEffect(() => {
    let seekToValue = null;
    if (multiplyMode && active) {
      if (trimmingOn) {
        seekToValue = trimmingProgress * realDuration;
        setProgress({ played: parseFloat(trimmingProgress) });
      } else if (isZoomOpened) {
        seekToValue = (sceneData?.trimStart + videoDuration * zoomsSeekValue) / 1000;
      }
    } else if (!multiplyMode) {
      if (videoSeekToValue !== null) {
        seekToValue = videoSeekToValue.value;
        if (!playing && videoSeekToValue.playing) {
          setPlaying(true);
        }
        updateVideoSeekToValue(null);
        if (!hideControlsOnStart && hideControls) {
          mouseMovementRef.current.latestTimestamp = Date.now();
          setHideControls(false);
        }
      } else if (isCallToActionOpened) {
        seekToValue = callActionsSeekValue * (realDuration && isFinite(realDuration) ? realDuration : duration);
      } else if (isVoiceoverOpened) {
        seekToValue = voiceoverSeekValue * (videoDuration ? videoDuration / 1000 : realDuration && isFinite(realDuration) ? realDuration : duration);
      } else if (seekToSecondExternal !== null) {
        seekToValue = seekToSecondExternal;
      } else if (trimmingOn && trimmingProgress !== null) {
        seekToValue = trimmingProgress * realDuration;
        setProgress({ played: parseFloat(trimmingProgress) });
      }
    }

    seekToValue != null && player.current?.seekTo(parseFloat(seekToValue), 'seconds');
  }, [trimmingProgress, active, isZoomOpened, zoomsSeekValue, videoSeekToValue, callActionsSeekValue, seekToSecondExternal, voiceoverSeekValue]);

  useEffect(() => {
    if ((isCallToActionOpened || isVoiceoverOpened || addNotesMode) && playing) {
      togglePlaying();
    }
  }, [isCallToActionOpened, isVoiceoverOpened, addNotesMode]);

  useEffect(() => {
    if (isVoiceoverRecording) {
      if (!playing) {
        togglePlaying();
      }

      if (volume?.current !== 0) {
        toggleMuted();
      }
    } else {
      if (playing) {
        togglePlaying();
      }

      if (volume?.current === 0) {
        toggleMuted();
      }
    }
  }, [isVoiceoverRecording]);

  useEffect(() => {
    if (isVoiceoverCountdown && playing) {
      togglePlaying();
    }
  }, [isVoiceoverCountdown]);

  useEffect(() => {
    if (player?.current) {
      // Instead of defining the video element, use the player video element hidden into the player ref
      videoElement.current = multiplyMode ? player?.current?.player?.player?.player : player.current.getInternalPlayer();
    }
  }, [player?.current]);

  useEffect(() => {
    if (trimmingOn && active) {
      playing && setPlaying(false);
      const startTrimStampSeconds = (startTrimStamp * (sceneData?.playbackRate || 1)) / 1000;
      if (playedSeconds < startTrimStampSeconds) {
        player.current?.seekTo(parseFloat(startTrimStampSeconds), 'seconds');
      }
    }
  }, [startTrimStamp, active]);

  useEffect(() => {
    if (isChaptersEditor) {
      updateChapterStart(playedSeconds);
    }

    if (active) {
      const calcPlayedSeconds = playedSeconds * 1000;
      if (trimmingOn || isZoomOpened) {
        if (calcPlayedSeconds >= endTrimStamp * (sceneData?.playbackRate || 1)) {
          if (controlInUse) {
            player.current?.seekTo(parseFloat((endTrimStamp * (sceneData?.playbackRate || 1)) / 1000), 'seconds');
          } else {
            player.current?.seekTo(parseFloat((startTrimStamp * (sceneData?.playbackRate || 1)) / 1000), 'seconds');
          }
          setPlaying(false);
          handleTogglePlaying(false);
        } else if (calcPlayedSeconds < (startTrimStamp - trimmingDeviation) * (sceneData?.playbackRate || 1)) {
          player.current?.seekTo(parseFloat((startTrimStamp * (sceneData?.playbackRate || 1)) / 1000), 'seconds');
          setPlaying(false);
          handleTogglePlaying(false);
        }
      } else {
        /**
         * If video progress more than trimEnd received from the API - move video progress to start video (trimmed or not)
         * */
        const lessThanTrimStart = sceneData?.trimStart - calcPlayedSeconds > trimmingDeviation;
        const moreThanTrimEnd = calcPlayedSeconds >= sceneData?.trimEnd - trimmingDeviation || played >= 1;
        if (sceneData && sceneData.trimEnd !== sceneData.trimStart && (lessThanTrimStart || moreThanTrimEnd)) {
          if (player?.current?.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && moreThanTrimEnd) {
            setPlaying(false);
            active && onEnded();
          }

          if (multiplyMode) {
            // If only one scene is available, then after the end of playback there is no new scene to switch
            // Therefore, let's set the current scene to trimStart manually
            if (active && isSingleAvailableScene) {
              player.current?.seekTo(parseFloat(sceneData.trimStart / 1000), 'seconds');
            }
          } else {
            // For single player mode, in case trimStart value is available, meaning the original file is playing
            // seek to trimStart if current playing value is less then trimStart
            if (sceneData.trimStart && lessThanTrimStart) {
              player.current?.seekTo(parseFloat(sceneData.trimStart / 1000), 'seconds');
            }
            if (sceneData.trimEnd && moreThanTrimEnd) {
              setPlaying(false);
            }
          }
        }
      }
    }
  }, [playedSeconds]);

  useEffect(() => {
    if (sceneData && duration && active) {
      if (sceneData.trimEnd > 0 && sceneData.trimStart !== sceneData.trimEnd) {
        if (!trimmingOn) {
          updateEndTrimStamp(sceneData.trimEnd / (sceneData?.playbackRate || 1));
          updateStartTrimStamp(sceneData.trimStart / (sceneData?.playbackRate || 1));
        }
      } else {
        if (!multiplyMode) {
          if (!isHlsSource && duration && sceneData.trimStart) {
            // In case original video should be played, in case trimming were applied, seekTo trimStart
            // value, to be sure player start from the right point
            player.current.seekTo(sceneData.trimStart / 1000, 'seconds');
          } else {
            isFinite(realDuration) && setDuration(realDuration);
          }
        }

        if (sceneData.trimStart === sceneData.trimEnd) {
          sceneData.trimEnd > 0 && Sentry.captureMessage(`Player catches trimStart === trimEnd for uuid: ${uuid}, trimStart=${sceneData.trimStart}, trimEnd=${sceneData.trimEnd}`);
          updateStartTrimStamp(0);
        } else {
          updateStartTrimStamp(sceneData.trimStart / (sceneData?.playbackRate || 1));
        }
      }
    }
    if (!sceneData && (videoDuration || realDuration) && !duration) {
      setDuration(isFinite(realDuration) && realDuration ? realDuration : videoDuration / 1000);
    }
    if (!sceneData && !videoDuration && realDuration && duration) {
      setDuration(realDuration);
    }
  }, [sceneData, realDuration, videoDuration, active]);

  useEffect(() => {
    if (!trimmingOn && videoTrimmed && playing) {
      setPlaying(false);
    }
    if (videoTrimmed && !playing) {
      updateVideoTrimmed(false);
    }
  }, [videoTrimmed]);

  useEffect(() => {
    if (!playing && videoTrimmed) {
      handleTogglePlaying(true);
      updateVideoTrimmed(false);
    }
  }, [playing]);

  useEffect(() => {
    if (enableSubtitles && subtitles) {
      const appLanguageCode = appLanguage.lng.substring(0, 2);

      let languageToUse = '';
      for (const key in subtitles) {
        if (key.substring(0, 2) === appLanguageCode) {
          languageToUse = key;
          break;
        }

        if (subtitles[key].original) {
          languageToUse = key;
        }
      }

      if (languageToUse === '') {
        languageToUse = Object.keys(subtitles)[0];
      }

      setCurrentLanguage(languageToUse);
    }
  }, [player.current, subtitles, enableSubtitles]);

  useEffect(() => {
    active && controlsVisibilityChanged(!hideControls);
  }, [hideControls]);

  useEffect(() => {
    handleVolumeUpdated && handleVolumeUpdated(volume);
  }, [volume]);

  // Handle external parameters change
  useEffect(() => {
    if (externalVolume && externalVolume.current !== volume.current) {
      setVolume(externalVolume);
    } else if (externalPlaybackRate && externalPlaybackRate !== playbackRate) {
      setPlaybackRate(externalPlaybackRate);
    } else if (isFirstPlayedExternal && !firstPlay.current) {
      firstPlay.current = true;
    } else if (externalLanguage !== currentLanguage) {
      setCurrentLanguage(externalLanguage);
    }
  }, [externalVolume, externalPlaybackRate, isFirstPlayedExternal, externalLanguage]);

  useEffect(() => {
    if (playing) {
      setPlaying(false);
      handleTogglePlaying(false);
    }
  }, [blockPlaying]);

  useEffect(() => {
    if (active) {
      updateTrimmingVideoDuration((realDuration ?? (videoDuration / 1000)) / (sceneData?.playbackRate || 1));
    }
  }, [trimmingOn, active, realDuration]);

  useEffect(() => {
    if (active && playbackRate) {
      setOveralPlaybackRate(playbackRate * (sceneData?.playbackRate || 1));
    }
  }, [active, playbackRate, sceneData?.playbackRate]);

  useEffect(() => {
    if (active) {
      playOnStart &&
        !playing &&
        player.current.play().catch(e => {
          console.log('playOnStart was interrupted', e);
        });
      if (!playOnStart && !hideControlsOnStart) {
        setHideControls(false);
      }
      if (processing) {
        setHideControls(true);
      }
      handleProgressChange(playedSeconds);
    } else {
      setTimeout(() => {
        setPlaying(false);
        player.current?.seekTo(parseFloat((sceneData?.trimStart ?? 0) / 1000), 'seconds');
      }, 50);
    }
  }, [active, src, playOnStart]);

  useEffect(() => {
    if (active) {
      // For some reason the player is ignoring the set playbackRate when switching between players for hls video
      // Therefore, we are forced to temporarily change the value
      // and a bit later (I believe the player manages to set up the hls stream) set the real playbackRate value
      setOveralPlaybackRate(1);
      setTimeout(() => {
        setOveralPlaybackRate(playbackRate * (sceneData?.playbackRate || 1));
      }, 100);
    } else {
      setRenderingProcess(null);
      if (renderingDoneRef.current) {
        clearTimeout(renderingDoneRef.current);
        renderingDoneRef.current = null;
      }
    }
  }, [active]);

  useEffect(() => {
    if (active) {
      if (isDraft) {
        setRenderingProcess(null);
      } else {
        if (processing) {
          setRenderingProcess(RENDERING_PROCESS.PENDING);
        } else if (!processing && renderingProcess === RENDERING_PROCESS.PENDING) {
          setRenderingProcess(RENDERING_PROCESS.SHOW_DONE);
          renderingDoneRef.current = setTimeout(() => {
            setRenderingProcess(null);
            renderingDoneRef.current = null;
          }, 3000);
        } else if (!processing && status === 'FAILED') {
          setRenderingProcess(null);
        }
      }
    }
  }, [processing]);

  useEffect(() => {
    if (renderingProcess === RENDERING_PROCESS.PENDING && !isDraft) {
      setRenderingProcess(null);
    }
  }, [uuid])

  useEffect(() => {
    if (!showError && !isDraft && active && renderingProcess === null && !blockPlaying && !playing) {
      let show = false;
      if (firstPlay.current) {
        show = !buffering && !processing;
      } else {
        show = !isMobileView && !hidePlayControl && !onlyPlayControlShow && rerecordComponent !== null;
      }
      updateShowRerecording(show);
    } else {
      updateShowRerecording(false);
    }
  }, [
    active,
    renderingProcess,
    buffering,
    processing,
    showError,
    isDraft,
    isMobileView,
    hidePlayControl,
    onlyPlayControlShow,
    firstPlay,
    playing,
    blockPlaying,
  ]);

  // Control background music playing
  useEffect(() => {
    if (handleStopBackgroundMusic && (!poster || ((playing && buffering) || error))) {
      handleStopBackgroundMusic();
    }
    if (handleStartBackgroundMusic && playing && !buffering) {
      handleStartBackgroundMusic();
    }
  }, [buffering, poster, playing, error]);

  useEffect(() => {
    if (isIOSDevice) {
      const metaTag = document.querySelector(`meta[name="theme-color"]`);
      if (metaTag) {
        if (!metaTag?.dataset?.reactHelmet) {
          metaTag.dataset.reactHelmet = true;
        }
      }
    }
  }, [isIOSDevice]);

  useEffect(() => {
    if (!multiplyMode && player?.current?.getInternalPlayer()) {
      // Mute audio track without affecting the volume state
      if (directPlayerVolume !== null) {
        player.current.getInternalPlayer().volume = Math.min(directPlayerVolume * volume.current, 1);
      } else {
        if (player.current?.getInternalPlayer()?.volume !== volume.current) {
          player.current.getInternalPlayer().volume = volume.current;
        }
      }
    }

  }, [directPlayerVolume, volume.current])

  useEffect(() => {
    if (addNotesMode && !firstPlay.current) {
      firstPlay.current = true;
    }
  }, [addNotesMode])

  useEffect(() => {
    if (multiplyMode && playerParent && (zooms?.length > 0 || sceneData?.zoomPoints?.length > 0) && !resizeObserverRef?.current) {
      resizeObserverRef.current = new ResizeObserver(throttle((entries) => {
        for (const entry of entries) {
          const video = entry.target.querySelector('video');
          if (!video) {
            return;
          }
          let videoRatio = video.videoWidth / video.videoHeight;
          let contentWidth = 0;
          let contentHeight = 0;
          const DEFAULT_PLAYER_RATIO = 16 / 9;
          if (videoRatio <= DEFAULT_PLAYER_RATIO) {
            contentWidth = entry.target.offsetHeight * videoRatio;
            contentHeight = entry.target.offsetHeight;
          } else {
            contentWidth = entry.target.offsetWidth;
            contentHeight = entry.target.offsetWidth / videoRatio
          }
          const leftOffset = Math.round((video.offsetWidth - contentWidth) / 2);
          const topOffset = Math.round((video.offsetHeight - contentHeight) / 2);
          const margins = {
            x: {
              min: (leftOffset / entry.target.offsetWidth) * 100,
              max: ((leftOffset + contentWidth) / entry.target.offsetWidth) * 100,
            },
            y: {
              min: (topOffset / entry.target?.offsetHeight) * 100,
              max: ((topOffset + contentHeight) / entry.target.offsetHeight) * 100,
            }
          }
          setVideoContentDimensions({ leftOffset, topOffset, margins });
        }
      }));
      resizeObserverRef.current.observe(playerParent);
    }
    if ((zooms.length == 0 && sceneData?.zoomPoints?.length == 0) && resizeObserverRef?.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }
    return () => {
      if (resizeObserverRef?.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    }
  }, [zooms, sceneData?.zoomPoints, playerParent])

  return (
    <PlayerBackground
      className={cx(styles.videoPlayer, { [styles.iosFullscreen]: iosFullscreen, [styles.withoutBorder]: playerOnly }, moduleClassName)}
      onKeyUp={keyboardBehavior}
      onMouseMove={mouseMoveHandler}
      onMouseEnter={handleOnMouseOver}
      onMouseLeave={handleOnMouseLeave}
      isMobile={isMobileView}
      style={currentZoomStyles ? { cursor: 'pointer' } : {}}
    >
      {isIOSDevice && (
        <Helmet type="text/css">
          {iosFullscreen && <meta name="apple-mobile-web-app-capable" content="yes" />}
          {iosFullscreen && <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />}
          {iosFullscreen && <style>{`body { background-color: #000 !important; }`}</style>}
          {iosFullscreen && <meta name="theme-color" content="#000000" />}
        </Helmet>
      )}
      {!isMobileView && <InPlayerCommentsOverlay isFirstPlayed={firstPlay.current} playing={playing} containerElementNode={playerParent} maxDimensions={videoMaxDimensions} mixedOrientation={mixedOrientation} />}
      <div
        className={cx(styles.playerWrapper, playerWrapperClassName, {
          [styles.hiddenControls]: !isIOSDevice,
          [styles.isIOSDevice]: isIOSDevice,
          [styles.iosFullscreen]: iosFullscreen,
          [styles.disableIOSsubtitles]: !(isIOSDevice && videoElement?.current?.webkitDisplayingFullscreen),
          [styles.newNoteMode]: addNotesMode,
        })}
        ref={handleNodeRef}
      >
        {!error && (
          <>
            {containsAIContent && (
              <div className={cx(styles.aiActBadge, { [styles.fadeOnPlay]: playing })}>
                <InfoTooltip
                  text={aiDisclosureTooltip}
                  icon={<img src={CE_EU_AI_ACT_icon} alt="" />}
                  containerRef={player.current}
                  fixedPosition={true}
                  position="right"
                />
              </div>
            )}
            {multiplyMode ? (
              <EmptyPlayer
                id={uuid}
                active={active}
                scenes={sceneData}
                innerRef={player}
                width="100%"
                className={cx(styles.player, playerClassName)}
                wrapperClassName={styles.emptyPlayerWrapper}
                wrapperStyle={{ ...(currentZoomStyles ? { clipPath: `inset(${videoContentDimensions?.topOffset ?? 0}px ${videoContentDimensions?.leftOffset}px)` } : {}) }}
                poster={poster}
                height={playerOnly ? `${height}px` : '100%'}
                playing={playing}
                playbackRate={overallPlaybackRate}
                volume={Math.min(volume.current * videoVolumeWithMusic, 1)}
                muted={!volume.current}
                onReady={onReady}
                onStart={onStart}
                onPlay={onPlay}
                onPause={onPause}
                onBuffer={() => setBuffering(true)}
                onBufferEnd={() => setBuffering(false)}
                onSeek={onSeek}
                onEnded={onEndedCallback}
                onError={(e, data) => console.log('onError', e, data)}
                onProgress={onProgress}
                onDuration={onDuration}
                progressInterval={50}
                style={{ padding: playerOnly ? '0' : '' }}
                currentZoomStyles={currentZoomStyles}
              />
            ) : (
              <ReactPlayer
                key={src}
                id={uuid}
                autoPlay={playOnLoad}
                loop={playOnLoad}
                className={cx(
                  styles.player,
                  {
                    [styles.wideScreens]: isExtraWideScreen && screenfull.isFullscreen,
                  },
                  playerClassName
                )}
                ref={player}
                width="100%"
                height={playerOnly ? `${height}px` : '100%'}
                url={src}
                pip={pip}
                playing={playing}
                controls={native}
                light={light}
                playbackRate={overallPlaybackRate}
                volume={Math.min(volume.current * videoVolumeWithMusic, 1)}
                muted={!volume.current}
                onReady={onReady}
                onStart={onStart}
                onPlay={onPlay}
                onPause={onPause}
                onBuffer={handleBufferingStarted}
                onBufferEnd={handleBufferingEnded}
                onSeek={onSeek}
                onEnded={onEndedCallback}
                onError={(e, data) => console.log('onError', e, data)}
                onProgress={onProgress}
                onDuration={onDuration}
                progressInterval={50}
                bufferinginterval={300}
                style={{ padding: playerOnly ? '0' : '' }}
                playsinline={isIOSDevice && !screenfull.isFullscreen}
                {...additionalProps}
              />
            )}
            {((!blockSimulatedResources && textPanels?.length > 0) || editorSceneId === uuid) && active ? (
              <VideoTextPanel
                parentNode={playerParent}
                templates={videoConfig.textPanelTemplates}
                id={uuid}
                textPanelsArray={textPanels}
                videoSecondsProgress={(playedSeconds / (sceneData?.playbackRate || 1)) * 1000}
                multiplyMode={multiplyMode}
              />
            ) : null}
            {!blockSimulatedResources && logo && (
              <VideoLogo
                parentNode={playerParent}
                logo={logo}
                id={uuid}
                played={playedSeconds * 1000}
                intro={intro}
                outro={outro}
                duration={videoDuration ?? realDuration * 1000}
                mixedOrientation={mixedOrientation}
                videoMaxDimensions={videoMaxDimensions}
              />
            )}
            {!blockSimulatedResources && !multiplyMode && currentCallAction && (isCallToActionOpened || started) && (
              <VideoCallToAction
                parentNode={playerParent}
                callAction={currentCallAction}
                id={uuid}
                currentSeconds={playedSeconds * 1000}
                isFullScreen={screenfull.isFullscreen || iosFullscreen}
                mixedOrientation={mixedOrientation}
                videoMaxDimensions={videoMaxDimensions}
                onPauseCallback={() => {
                  setPlaying(false);
                  handleTogglePlaying(false);
                }}
                isMobileView={isMobileView && !isEmbeddedMode}
                isControlsVisible={(!hideControls || !playing) && firstPlay.current}
                isNewActionTimeline={isNewActionTimeline}
                isSharedPage={isSharedPage}
              />
            )}
            {!multiplyMode && started && (
              <VoiceoverPlayer
                currentSeconds={playedSeconds * 1000}
                mainVolume={volume?.current}
                playing={playing && !buffering}
                isSeek={seeking?.current}
                playbackRate={playbackRate}
              />
            )}
            <PlayerControls
              isReady={ready}
              playerNode={playerParent}
              id={uuid}
              playerOnly={playerOnly}
              seeking={seeking}
              onlyPlayControlShow={
                (onlyPlayControlShow && !hidePlayControl) ||
                blockPlaying ||
                ((multiplyMode ? isFirstPlayedExternal : firstPlay.current) &&
                  (hideControlsOnStart ? scenes?.trimStart - playedSeconds < 100 && hideControls : false))
              }
              native={native}
              error={error}
              showOverlay={showOverlay}
              isMobileView={isMobileView}
              onSeekMouseDown={onSeekMouseDown}
              onSeekChange={onSeekChange}
              onSeekMouseUp={onSeekMouseUp}
              togglePlaying={togglePlaying}
              playing={playing}
              handleChangeVolume={handleChangeVolume}
              toggleMuted={toggleMuted}
              volume={volume}
              hideControls={hideControls || (!multiplyMode && !src)}
              hideControlsOnStart={hideControlsOnStart}
              hideSlider={hideSlider}
              played={played}
              duration={durationToControls}
              calculatedPlayedValue={calculatedPlayedValue}
              disableDownload={disableDownload}
              downloadNotification={downloadNotification}
              onDownloadVideo={onDownloadVideo}
              toggleFullscreen={toggleFullscreen}
              sliderWidth={sliderWidth}
              isFullScreenAllowed={isFullScreenAllowed && !isVoiceoverOpened && !isCallToActionOpened}
              isFullScreen={screenfull.isFullscreen || iosFullscreen}
              currentLanguage={currentLanguage}
              availableLanguages={
                subtitles && Object.keys(subtitles).length > 0
                  ? externalAvailableLanguages
                    ? externalAvailableLanguages
                    : Object.keys(subtitles)
                  : null
              }
              onLanguageSelected={onLanguageSelected}
              subtitles={subtitles}
              subtitlesSettings={subtitlesSettings}
              showSubtitlesExistance={showSubtitlesExistance}
              isSubtitlesExist={subtitles && Object.keys(subtitles).length > 0}
              customTiming={customTiming}
              isFirstPlay={firstPlay.current}
              enableSubtitles={enableSubtitles}
              showSubtitlesControlExternal={showSubtitlesControlExternal}
              isEmbeddedMode={isEmbeddedMode}
              playbackRate={playbackRate}
              handleChangePlaybackRate={handleChangePlaybackRate}
              isBackwardAndForwardActionsAllows={!isVoiceoverRecording && !isVoiceoverCountdown && isBackwardAndForwardActionsAllows}
              videoConfig={videoConfig}
              companyConfig={companyConfig}
              hasSrc={multiplyMode || !!src}
              videoMaxDimensions={videoMaxDimensions}
              mixedOrientation={mixedOrientation}
              blockSimulatedResources={blockSimulatedResources}
              processing={processing}
            />
            {!blockSimulatedResources && !trimmingOn && transitionsArray && (
              <TransitionSimulation
                transitions={transitionsArray}
                playedMs={videoElement.current?.currentTime * 1000}
                parentNode={playerParent}
                videoMaxDimensions={videoMaxDimensions}
                isIOS={isIOSDevice}
                isFullScreen={iosFullscreen}
                isMobileView={isMobileView}
              />
            )}

            {ready && fadeIn && (
              <FadeInTransition
                start={(videoDuration ? videoDuration : realDuration * 1000) - FADE_IN_DURATION + (sceneData?.trimStart ?? 0)}
                end={(videoDuration ? videoDuration : realDuration * 1000) + (sceneData?.trimStart ?? 0)}
                playedMs={playedSeconds * 1000}
              />
            )}
          </>
        )}

        {!showError && renderingProcess && (
          <div className={styles.processContainer}>
            <div className={styles.processContent}>
              <ProgressLogo isRenderingDone={isRenderingDone} />
              <div className={styles.dotsContainer}>
                <div className={cx(styles.loading, { [styles.hidden]: isRenderingDone })}>
                  <div style={{ display: 'flex' }}>
                    <span className={styles.text}>{processingMessage || t('my_video.video_player.saving_video')}</span>
                    <span className={styles.dots} />
                  </div>
                  <p className={styles.safeText}>{t('my_video.video_player.safe_to_leave')}</p>
                </div>
                <>
                  <div className={cx(styles.loading, { [styles.hidden]: !isRenderingDone })}>{t('my_video.video_player.enjoy')}</div>
                </>
              </div>
            </div>
          </div>
        )}
        {buffering && !isDraft && (
          <div className={styles.spinnerContainer}>
            <div className={styles.spinnerContent}>
              <LoadingSpinner theme="light" />
            </div>
          </div>
        )}
        <div className={cx(styles.player, { [styles.hide]: !error })}>{errorComponent}</div>
        <div>
          <div
            className={cx(styles.firstPlayTrigger, {
              [styles.hide]: error,
              [styles.playerOverlay]: showOverlay || (!hidePlayControl && onlyPlayControlShow) || isMobileView,
              [styles.imageForPlayerOnlyInMobile]: playerOnly && isMobileView,
              [styles.disabled]: isDraft || showError,
              [styles.hideCursor]: hideControls,
              [styles.downgrade]: isCallToActionOpened || firstPlay.current,
            })}
            role="button"
            onClick={showError ? undefined : handlePlayerClick}
            onMouseMove={mouseMoveHandler}
            onKeyDown={event => {
              if (event.keyCode === ENTER_KEY_CODE && !blockPlaying && !isVoiceoverRecording) togglePlaying();
            }}
            title={playing ? t('my_video.video_player.pause') : t('my_video.video_player.play')}
            tabIndex={0}
          >
            {!nextSceneTransition &&
              poster &&
              !playing &&
              !firstPlay.current &&
              !hidePlayControl &&
              isShowThumbnailWhileZoomOpened &&
              !isCallToActionOpened && (
                <PlayerThumbnail
                  active={active}
                  url={poster}
                  parentNode={playerParent}
                  className={styles.thumbnail}
                  isDraft={isDraft}
                  showError={showError}
                  currentZoomStyles={currentZoomStyles}
                />
              )}
            {!nextSceneTransition &&
              onlyResourceThumbnail &&
              !playing &&
              !firstPlay.current &&
              isShowThumbnailWhileZoomOpened &&
              !isCallToActionOpened && (
                <PlayerThumbnail
                  active={active}
                  url={onlyResourceThumbnail}
                  parentNode={playerParent}
                  className={styles.thumbnail}
                  isDraft={isDraft}
                  showError={showError}
                  currentZoomStyles={currentZoomStyles}
                />
              )}
            {ready &&
              !buffering &&
              !showError &&
              !isDraft &&
              !isMobileView &&
              !hidePlayControl &&
              !onlyPlayControlShow &&
              !firstPlay.current &&
              !playing &&
              !blockPlaying &&
              !isCallToActionOpened && (
                <div className={styles.firstPlayIconContainer}>
                  <RoundedPlayIcon style={{ zIndex: 1 }} />
                  {!isVoiceoverOpened && <InitialVideoSpeedMenu playbackRate={playbackRate} handleChangePlaybackRate={handleChangePlaybackRate} />}
                  <div
                    className={cx(styles.rerecordContainer, {
                      [styles.rerecordShow]: showRerecording,
                    })}
                  >
                    {rerecordComponent}
                  </div>
                </div>
              )}
            {!showError && !isDraft && !playing && !blockPlaying && ((!hidePlayControl && onlyPlayControlShow) || isMobileView) && (
              <div className={styles.firstPlayIconContainer}>
                <RoundedPlayIcon style={{ zIndex: 1 }} />
                {!isMobileView && !isVoiceoverOpened && (
                  <InitialVideoSpeedMenu playbackRate={playbackRate} handleChangePlaybackRate={handleChangePlaybackRate} />
                )}
              </div>
            )}
            {active && isDraft}

            {showOverlay && overlayContent ? (
              <div className={styles.videoMetadata}>
                <div>{overlayContent}</div>
                <div className={styles.videoDuration}>
                  {duration ? <Duration seconds={duration} showTimeUnit /> : t('my_video.video_player.calculating_duration')}
                </div>
              </div>
            ) : null}
          </div>
          {showError && !isDraft ? (
            <>
              <div className={styles.errorMessageContainer}>
                <p className={styles.errorMessage}>
                  {t(hasHls ? 'video.player.error_message_two' : 'video.player.error_message_one')}
                </p>
                <div className={styles.buttonsContainer}>
                  <CustomButton
                    label={t('general.contact_us')}
                    onClick={openCrisp}
                    variant="secondary"
                    className={styles.errorMessageButton}
                  />
                  <CustomButton
                    label={t('general.close')}
                    onClick={closeError}
                    variant="secondary"
                    className={styles.errorMessageButton}
                  />
                </div>
              </div>
            </>
          ) : null}
        </div>
        {isVideoEndCtaEnabled && onEndComponent && played >= 1 ? onEndComponent : null}
      </div>
      {firstPlay.current && (
        <div
          className={cx(styles.rerecordContainer, {
            [styles.rerecordShow]: showRerecording,
          })}
        >
          {rerecordComponent}
        </div>
      )}
      {isVoiceoverOpened && <Countdown currentSeconds={playedSeconds * 1000} duration={isFinite(realDuration) && realDuration ? realDuration * 1000 : videoDuration} />}
    </PlayerBackground>
  );
}
